import { fabric } from 'fabric';
import { colorToRgba } from './colorUtils';

/**
 * Paint one image + its annotations onto the fabric canvas.
 *
 * The canvas element is sized by its container (see useCanvasViewport), NOT by
 * the image: the image is placed at its original pixel size in canvas space and
 * the viewport transform does the scaling. Stroke widths are therefore divided
 * by the zoom so they stay ~2 screen px whether the image is 96px or 6000px wide.
 *
 * `createBoxObject` / `createPolygonObject` are the single place a fabric shape
 * is built from a stored annotation — loading an image and pasting from the
 * clipboard both go through them, so the two can never drift apart in style or
 * in coordinate convention.
 */

const SELECTION_STYLE = {
  transparentCorners: false,
  cornerColor: '#ffffff',
  cornerStrokeColor: '#111111',
  cornerSize: 9,
  cornerStyle: 'circle',
  borderScaleFactor: 1.5,
  padding: 0,
  strokeUniform: true,
  objectCaching: false,
};

export const buildSelectionStyle = () => ({ ...SELECTION_STYLE });

/**
 * Shared drawing context for the builders below.
 * @typedef {{width:number, height:number, classes:Array, zoom:number,
 *            baseStroke:number, showFill:boolean}} DrawContext
 */

/**
 * Rect from a normalised annotation ({centerX, centerY, width, height, angle}).
 * `left`/`top` are the un-rotated top-left corner and the shape rotates about
 * it — the same convention useObjectModifications reads back.
 */
export const createBoxObject = (ann, annIndex, ctx) => {
  const { width, height, classes, zoom = 1, baseStroke = 2, showFill = false } = ctx;
  const selectedClass = classes?.find((cls) => cls.id === ann.classId);
  const strokeColor = selectedClass?.color || 'red';
  const isAiSuggestion = !!ann.isAI;

  const boxWidth = ann.width * width;
  const boxHeight = ann.height * height;

  const rect = new fabric.Rect({
    left: ann.centerX * width - boxWidth / 2,
    top: ann.centerY * height - boxHeight / 2,
    width: boxWidth,
    height: boxHeight,
    angle: ann.angle || 0,
    // AI suggestions render dashed with a light fill until accepted/edited
    fill: isAiSuggestion
      ? colorToRgba(strokeColor, 0.12)
      : showFill
        ? colorToRgba(strokeColor, 0.14)
        : 'transparent',
    stroke: strokeColor,
    strokeWidth: baseStroke / zoom,
    strokeDashArray: isAiSuggestion ? [8 / zoom, 5 / zoom] : null,
    selectable: true,
    ...SELECTION_STYLE,
  });

  rect.data = { annIndex, type: 'bbox', isAI: isAiSuggestion };
  rect.classData = selectedClass;
  return rect;
};

/** Polygon from a normalised annotation ({points: [{x, y}] in 0..1}). */
export const createPolygonObject = (poly, polyIndex, ctx) => {
  const { width, height, classes, zoom = 1, baseStroke = 2, showFill = false } = ctx;
  const selectedClass = classes?.find((cls) => cls.id === poly.classId);

  const polygon = new fabric.Polygon(
    poly.points.map((p) => ({ x: p.x * width, y: p.y * height })),
    {
      fill: colorToRgba(selectedClass?.color, showFill ? 0.35 : 0.25),
      stroke: selectedClass?.color || 'red',
      strokeWidth: baseStroke / zoom,
      selectable: true,
      ...SELECTION_STYLE,
    }
  );

  polygon.data = { polyIndex, type: 'polygon' };
  polygon.classData = selectedClass;
  return polygon;
};

export const loadImageToCanvas = (
  imgData,
  index,
  canvas,
  setError,
  classes,
  BASE_STROKE_WIDTH,
  options = {}
) => {
  const { zoom = 1, showFill = false, onLoaded } = options;

  if (!imgData) {
    setError('Image data not available.');
    return;
  }

  if (!(canvas instanceof fabric.Canvas)) {
    setError('Canvas is not initialized as a fabric.Canvas');
    return;
  }

  const source = imgData.dataUrl || imgData.url_display || imgData.url;
  if (!source) {
    setError(`Missing image source for: ${imgData?.fileName}`);
    return;
  }

  canvas.clear();

  fabric.Image.fromURL(
    source,
    (img) => {
      if (!img?.width || !img?.height) {
        setError(`Failed to load image: ${imgData?.fileName}`);
        return;
      }

      // Annotations are normalised against originalWidth/Height. Fall back to
      // the decoded size when the record has none, and always rescale the
      // bitmap onto that box so a thumbnail URL can never shift coordinates.
      const width = Number(imgData.originalWidth) || img.width;
      const height = Number(imgData.originalHeight) || img.height;

      const drawContext = {
        width,
        height,
        classes,
        zoom: zoom || 1,
        baseStroke: BASE_STROKE_WIDTH,
        showFill,
      };

      img.set({
        left: 0,
        top: 0,
        scaleX: width / img.width,
        scaleY: height / img.height,
        selectable: false,
        evented: false,
      });

      canvas.setBackgroundImage(
        img,
        () => {
          (imgData.annotations || []).forEach((ann, annIndex) => {
            canvas.add(createBoxObject(ann, annIndex, drawContext));
          });

          (imgData.polygons || []).forEach((poly, polyIndex) => {
            canvas.add(createPolygonObject(poly, polyIndex, drawContext));
          });

          canvas.requestRenderAll();
          onLoaded?.({ width, height });
        },
        { crossOrigin: 'anonymous' }
      );
    },
    { crossOrigin: 'anonymous' }
  );
};
